import React, { useState } from "react";
import { Section, Eyebrow, SectionTitle, currency } from "@/components/Primitives";
import { Button } from "@/components/ui/button";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Check, Star, Gavel, Users, Percent, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";

const GROUPS = {
  Homeowner: [
    { name: "Free", base: null, m: 0, blurb: "Post jobs & receive bids", features: ["Unlimited job postings", "Receive up to 7 contractor bids", "Milestone progress tracking", "Escrow-backed payments"] },
    { name: "Gold", base: "homeowner_gold", m: 29, blurb: "Access verified contractors", tier: "gold", features: ["Everything in Free", "Full access to verified contractors", "Priority WSIB & insured pros", "Direct messaging"] },
    { name: "Platinum", base: "homeowner_platinum", m: 59, blurb: "Access realtors & investors", tier: "platinum", features: ["Everything in Gold", "Access investors buying, selling & investing", "Off-market property connections", "Concierge project support"] },
  ],
  Contractor: [
    { name: "Free", base: null, m: 0, blurb: "Get started", features: ["Public profile", "Browse open leads", "Pay-per-lead bidding", "Standard connection fees"] },
    { name: "Gold", base: "contractor_gold", m: 49, blurb: "For growing contractors", tier: "gold", popular: true, features: ["Verified badge", "Priority lead alerts", "Lower lead & commission fees", "Featured placement"] },
    { name: "Platinum", base: "contractor_platinum", m: 99, blurb: "For established firms", tier: "platinum", features: ["Everything in Gold", "Lowest fees & commission", "Team member seats", "CRM & API access"] },
  ],
  "General Labor": [
    { name: "Free", base: null, m: 0, blurb: "Find work", features: ["Public profile", "Browse gigs", "Ratings & reviews"] },
    { name: "Gold", base: "labor_gold", m: 19, blurb: "Find steady gigs", tier: "gold", features: ["Gig alerts near you", "Skill-based matching", "Direct contractor chat", "Priority visibility"] },
  ],
  "Realtor / Investor": [
    { name: "Free", base: null, m: 0, blurb: "List & browse", features: ["1 active listing", "Browse the marketplace", "Contractor rehab estimates"] },
    { name: "Gold", base: "investor_gold", m: 59, blurb: "For active agents", tier: "gold", features: ["Unlimited listings", "ARV & cap-rate analytics", "Buyer connections", "Deal flow feed"] },
    { name: "Platinum", base: "investor_platinum", m: 99, blurb: "For serious investors", tier: "platinum", features: ["Everything in Gold", "Off-market deal flow", "Priority buyer connections", "Dedicated deal manager"] },
  ],
  "Cargo Driver": [
    { name: "Free", base: null, m: 0, blurb: "Browse runs", features: ["Public profile", "Browse delivery board", "Keep 100% of payouts"] },
    { name: "Gold", base: "driver_gold", m: 29, blurb: "For active drivers", tier: "gold", features: ["Priority delivery runs", "Instant claim alerts", "Route optimization", "Featured driver badge"] },
  ],
};

const Pricing = () => {
  const { user } = useAuth();
  const [annual, setAnnual] = useState(false);

  const subscribe = async (plan) => {
    if (!plan.base) { toast.info("This is a free plan — just sign up!"); return; }
    if (!user) { toast.error("Please log in or sign up first"); return; }
    const lookup = `${plan.base}_${annual ? "annual" : "monthly"}`;
    try {
      const { data } = await api.post("/payments/checkout", { lookup_key: lookup, origin_url: window.location.origin, context: `plan:${lookup}` });
      window.location.href = data.checkout_url;
    } catch (e) { toast.error(e.response?.data?.detail || "Could not start checkout"); }
  };

  const price = (m) => annual ? m * 10 : m;
  const period = annual ? "/yr" : "/mo";

  return (
    <div>
      <div className="eti-grid-bg border-b border-white/10 py-14">
        <Section className="text-center">
          <Eyebrow>Membership Plans</Eyebrow>
          <h1 className="font-display text-4xl sm:text-5xl font-extrabold uppercase tracking-tight text-white leading-[1.05]">Gold & Platinum Tiers</h1>
          <p className="mt-4 text-slate-300 max-w-2xl mx-auto">Transparent pricing for every role. Upgrade to unlock connections, lower fees and premium visibility. Cancel anytime.</p>
          <div className="mt-7 inline-flex items-center gap-1 bg-secondary rounded-full p-1 border border-white/10" data-testid="billing-toggle">
            <button onClick={() => setAnnual(false)} className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${!annual ? "bg-primary text-white" : "text-slate-300"}`} data-testid="billing-monthly">Monthly</button>
            <button onClick={() => setAnnual(true)} className={`px-5 py-2 rounded-full text-sm font-semibold transition-colors ${annual ? "bg-primary text-white" : "text-slate-300"}`} data-testid="billing-annual">Annual <span className="text-emerald-400">· 2 months free</span></button>
          </div>
        </Section>
      </div>

      <Section className="py-14">
        <Tabs defaultValue="Contractor">
          <TabsList className="bg-secondary flex-wrap h-auto mx-auto">
            {Object.keys(GROUPS).map((r) => (
              <TabsTrigger key={r} value={r} className="data-[state=active]:bg-primary data-[state=active]:text-white" data-testid={`pricing-tab-${r.replace(/[ /]/g, "-").toLowerCase()}`}>{r}</TabsTrigger>
            ))}
          </TabsList>

          {Object.entries(GROUPS).map(([role, tiers]) => (
            <TabsContent key={role} value={role} className="mt-8">
              <div className={`grid gap-6 ${tiers.length === 2 ? "md:grid-cols-2 max-w-3xl mx-auto" : "md:grid-cols-3"}`}>
                {tiers.map((p) => (
                  <div key={p.name} className={`eti-card rounded-xl p-6 flex flex-col relative ${p.popular || p.tier === "platinum" ? "!border-primary" : ""}`} data-testid={`pricing-card-${role}-${p.name}`.replace(/[ /]/g, "-").toLowerCase()}>
                    {p.popular && <div className="absolute -top-3 left-1/2 -translate-x-1/2 bg-primary text-white text-[10px] font-bold uppercase tracking-widest px-3 py-1 rounded-full flex items-center gap-1"><Star className="h-3 w-3" />Most Popular</div>}
                    <div className="flex items-center gap-2">
                      <p className="font-display font-bold text-white uppercase tracking-wide">{p.name}</p>
                      {p.tier === "gold" && <span className="text-[10px] font-bold uppercase tracking-widest text-amber-400 border border-amber-400/40 rounded px-1.5 py-0.5">Gold</span>}
                      {p.tier === "platinum" && <span className="text-[10px] font-bold uppercase tracking-widest text-slate-200 border border-slate-300/40 rounded px-1.5 py-0.5">Platinum</span>}
                    </div>
                    <p className="text-sm text-slate-400 mt-1">{p.blurb}</p>
                    <div className="mt-4 flex items-end gap-1">
                      <span className="font-display text-4xl font-extrabold text-white">{p.m === 0 ? "Free" : currency(price(p.m))}</span>
                      {p.m !== 0 && <span className="text-slate-400 text-sm mb-1">{period}</span>}
                    </div>
                    <ul className="mt-5 space-y-2.5 flex-1">
                      {p.features.map((f, i) => (
                        <li key={i} className="flex items-start gap-2 text-sm text-slate-300"><Check className="h-4 w-4 text-primary shrink-0 mt-0.5" />{f}</li>
                      ))}
                    </ul>
                    <Button onClick={() => subscribe(p)} className={`mt-6 w-full font-semibold ${p.popular || p.tier === "platinum" ? "bg-primary hover:bg-primary/90 text-white" : "bg-secondary hover:bg-secondary/80 text-white border border-white/10"}`} data-testid={`pricing-subscribe-${role}-${p.name}`.replace(/[ /]/g, "-").toLowerCase()}>
                      {p.base ? `Choose ${p.name}` : "Sign Up Free"}
                    </Button>
                  </div>
                ))}
              </div>
            </TabsContent>
          ))}
        </Tabs>
      </Section>

      {/* Bidding / Leads explainer */}
      <Section className="pb-16">
        <div className="eti-card rounded-2xl p-8">
          <Eyebrow>How Job Leads Work</Eyebrow>
          <SectionTitle>The 7-Contractor Bidding System</SectionTitle>
          <p className="mt-4 text-slate-300 max-w-3xl leading-relaxed">
            When a homeowner posts a job — say a <span className="text-white font-semibold">$200,000 basement renovation</span> — it becomes an exclusive lead. A maximum of <span className="text-primary font-semibold">7 contractors</span> can unlock and bid on it. Contractors pay a one-time lead-access fee, and the one who wins the job pays Exclusive Trades a commission on the awarded value.
          </p>
          <div className="mt-8 grid sm:grid-cols-3 gap-4">
            <div className="bg-secondary rounded-lg p-5" data-testid="lead-explainer-0">
              <Users className="h-6 w-6 text-primary" />
              <p className="font-display font-bold text-white uppercase text-sm mt-3">Max 7 Bidders</p>
              <p className="text-sm text-slate-400 mt-1">Only 7 contractors can claim each lead — keeping competition high but fair.</p>
            </div>
            <div className="bg-secondary rounded-lg p-5" data-testid="lead-explainer-1">
              <Gavel className="h-6 w-6 text-primary" />
              <p className="font-display font-bold text-white uppercase text-sm mt-3">$50–$200 Lead Fee</p>
              <p className="text-sm text-slate-400 mt-1">Access fee scales with job size. Larger jobs like a $200k reno command a premium lead.</p>
            </div>
            <div className="bg-secondary rounded-lg p-5" data-testid="lead-explainer-2">
              <Percent className="h-6 w-6 text-primary" />
              <p className="font-display font-bold text-white uppercase text-sm mt-3">15–25% On Award</p>
              <p className="text-sm text-slate-400 mt-1">The awarded contractor pays 15–25% commission on the job value (lower % on bigger jobs).</p>
            </div>
          </div>
          <div className="mt-6 flex items-start gap-3 text-sm text-slate-400">
            <ShieldCheck className="h-5 w-5 text-primary shrink-0 mt-0.5" />
            <p>Lead fee tiers: under $5k → $50 · $5k–$25k → $100 · $25k–$100k → $150 · $100k+ → $200. Commission: $100k+ jobs 15% · $25k–$100k 20% · under $25k 25%.</p>
          </div>
        </div>
        <p className="text-center text-xs text-slate-500 mt-8 font-mono">Test mode — use card 4242 4242 4242 4242, any future expiry & CVC.</p>
      </Section>
    </div>
  );
};

export default Pricing;
